// 🔧 settings_managers.js
// ✅ Purpose: Admin-only Manager Settings page
// ✅ Features:
// - Standalone replacement for old Managers tab
// - Add manager by email
// - Creates pending manager user records when needed
// - Lists all current event managers
// - Prevents deletion of main event owner
// - Uses manageEventUser Cloud Function for manager add/remove operations
// - Cloud Function maintains /events/{eventId}/users/{uid}
// - Cloud Function maintains /events/{eventId}.managerIds
// - Cloud Function maintains /users/{uid}.eventInvites
// - Cloud Function writes immutable integrity audit records
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";

const functions = getFunctions();

const SettingsManagers = () => {
  const {
    eventId,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [managers, setManagers] = useState([]);
  const [ownerId, setOwnerId] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [savingManager, setSavingManager] = useState(false);

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    loadManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadManagers = async () => {
    if (!eventId) return;

    try {
      setLoadingManagers(true);

      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      const eventData = eventSnap.exists()
        ? eventSnap.data()
        : {};

      setOwnerId(eventData?.ownerId || null);

      const managerQuery = query(
        collection(db, "events", eventId, "users"),
        where("role", "==", "manager")
      );

      const managerSnap = await getDocs(managerQuery);
      const allManagers = [];

      for (const mgr of managerSnap.docs) {
        const data = mgr.data();
        const uid = mgr.id;

        const globalUserSnap = await getDoc(doc(db, "users", uid));
        const globalUser = globalUserSnap.exists()
          ? globalUserSnap.data()
          : {};

        allManagers.push({
          uid,
          email: globalUser?.email || data?.email || "(unknown)",
          name: `${globalUser?.firstName || ""} ${globalUser?.lastName || ""}`.trim(),
        });
      }

      setManagers(allManagers);
    } catch (err) {
      logger.error("❌ Failed to load managers:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load event managers.",
      });
    } finally {
      setLoadingManagers(false);
    }
  };

  const handleAddManager = async () => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      showStatus(setStatus, {
        type: "error",
        message: "Please enter an email address.",
      });

      return;
    }

    setSavingManager(true);

    try {
      const userSnap = await getDocs(
        query(
          collection(db, "users"),
          where("email", "==", trimmedEmail)
        )
      );

      let uid = null;
      let userData = null;

      if (!userSnap.empty) {
        const userDoc = userSnap.docs[0];

        uid = userDoc.id;
        userData = userDoc.data();
      } else {
        const pendingRef = doc(collection(db, "users"));

        uid = pendingRef.id;

        userData = {
          email: trimmedEmail,
          firstName: "",
          lastName: "",
          phone: "",
          role: "pending",
          userType: "pendingManager",
          loginMethod: null,
          emailVerified: false,
          inviteEmailSent: false,
          invitedAt: new Date().toISOString(),
        };

        await setDoc(pendingRef, userData);
      }

      const manageEventUser = httpsCallable(functions, "manageEventUser");

      await manageEventUser({
        eventId,
        targetUid: uid,
        role: "manager",
        permissions: {},
        removeUser: false,
        reason: "Manager added from Manager Settings page.",
      });

      showStatus(setStatus, {
        type: "success",
        message:
          userData?.userType === "pendingManager"
            ? "Pending manager invite created."
            : "Manager added successfully.",
      });

      setEmail("");

      await loadManagers();
    } catch (err) {
      logger.error("❌ Failed to add manager:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to add manager.",
      });
    } finally {
      setSavingManager(false);
    }
  };

  const handleRemoveManager = async (uid) => {
    if (uid === ownerId) {
      showStatus(setStatus, {
        type: "warning",
        message: "The primary event manager cannot be removed.",
      });

      return;
    }

    const confirmRemove = window.confirm(
      "Are you sure you want to remove this manager?"
    );

    if (!confirmRemove) return;

    setSavingManager(true);

    try {
      const manageEventUser = httpsCallable(functions, "manageEventUser");

      await manageEventUser({
        eventId,
        targetUid: uid,
        removeUser: true,
        reason: "Manager removed from Manager Settings page.",
      });

      setManagers((prev) =>
        prev.filter((manager) => manager.uid !== uid)
      );

      showStatus(setStatus, {
        type: "success",
        message: "Manager removed successfully.",
      });

      await loadManagers();
    } catch (err) {
      logger.error("❌ Failed to remove manager:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to remove manager.",
      });
    } finally {
      setSavingManager(false);
    }
  };

  if (
    !ready ||
    eventLoading ||
    isManager === null ||
    loadingManagers
  ) {
    return <FullPageSpinner message="Loading manager settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">👥 Managers</h1>
            <p className="lead text-muted mb-0">
              Manage event managers and permissions for this event.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleAddManager}
              disabled={!email.trim() || savingManager}
            >
              {savingManager ? "Saving..." : "➕ Add Manager"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Manager Tools</strong>
          </div>

          <div className="card-body">
            <label className="form-label">Add Manager by Email</label>

            <div className="row g-3">
              <div className="col-12 col-lg-8">
                <input
                  type="email"
                  className="form-control"
                  placeholder="manager@example.com"
                  value={email}
                  disabled={savingManager}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddManager();
                    }
                  }}
                />
              </div>

              <div className="col-12 col-lg-4">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleAddManager}
                  disabled={!email.trim() || savingManager}
                >
                  {savingManager ? "Saving..." : "➕ Add Manager"}
                </button>
              </div>
            </div>

            <div className="small text-muted mt-2">
              Existing users are added immediately. New users receive a pending manager record for this event.
            </div>
          </div>
        </div>

        {managers.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4>No additional managers assigned</h4>
              <p className="text-muted mb-4">
                Add another manager if someone else needs access to help configure or run this event.
              </p>
            </div>
          </div>
        ) : (
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <strong>Current Event Managers</strong>
              <div className="small text-muted">
                Managers can help administer this event.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-4">
                {managers.map((mgr) => {
                  const displayName = mgr.name || "(no name)";
                  const initials = displayName
                    .split(" ")
                    .filter(Boolean)
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();

                  const isOwner = mgr.uid === ownerId;

                  return (
                    <div key={mgr.uid} className="col-12 col-md-6">
                      <div className="card h-100 border shadow-sm">
                        <div className="card-body">
                          <div className="d-flex gap-3 align-items-start">
                            <div
                              className="rounded-circle bg-light border d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: "52px",
                                height: "52px",
                                fontWeight: "700",
                              }}
                            >
                              {initials || "?"}
                            </div>

                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between gap-3">
                                <div>
                                  <h5 className="mb-1">{displayName}</h5>
                                  <div className="small text-muted">
                                    {mgr.email}
                                  </div>
                                </div>

                                <span
                                  className={`badge ${
                                    isOwner
                                      ? "bg-warning text-dark"
                                      : "bg-primary"
                                  } align-self-start`}
                                >
                                  {isOwner ? "Owner" : "Manager"}
                                </span>
                              </div>

                              <div className="d-flex flex-wrap gap-2 mt-3">
                                <a
                                  href={`mailto:${mgr.email}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  ✉️ Email
                                </a>

                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={isOwner || savingManager}
                                  title={
                                    isOwner
                                      ? "Main event owner cannot be removed"
                                      : ""
                                  }
                                  onClick={() => handleRemoveManager(mgr.uid)}
                                >
                                  🗑 Remove
                                </button>
                              </div>

                              {isOwner && (
                                <div className="small text-muted mt-2">
                                  Primary event owner cannot be removed from this page.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SettingsManagers;